import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Button, Modal, Spin, Radio, InputNumber, Table, Select, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import LookupField from '../../components/LookupField';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import { useModulePermission } from '../../hooks/usePermission';
import { useRdSubcontractForm } from './hooks/useRdSubcontractForm';
import { rdSubcontractApi, ApprovalHistory } from '../../services/rdSubcontractApi';
import { BatchAcceptanceBridge, pwj215f1AcceptanceManifest } from '../../features/acceptance';

const { TextArea } = Input;

const MODULE_CODE = 'pwj215f1';

const statusLabels: Record<string, string> = {
  N: '未審核', P: '已核准', '2': '部長已審核', '3': '處長已審核',
  '4': '課長已審核', '5': '組長已審核',
};

/** Reusable ApprovalHistoryModal for J215 */
const ApprovalHistoryModal: React.FC<{
  visible: boolean;
  requestNo: string;
  onClose: () => void;
}> = ({ visible, requestNo, onClose }) => {
  const [data, setData] = React.useState<ApprovalHistory[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (visible && requestNo) {
      setLoading(true);
      rdSubcontractApi.getApprovalHistory(requestNo)
        .then(setData)
        .catch(() => setData([]))
        .finally(() => setLoading(false));
    }
  }, [visible, requestNo]);

  const actionLabels: Record<string, string> = {
    SUBMIT: '送審', APPROVE: '核准', REJECT: '退回',
  };

  const columns = [
    { title: '操作', dataIndex: 'action', key: 'action', width: 80,
      render: (v: string) => actionLabels[v] || v },
    { title: '執行人', dataIndex: 'actor_name', key: 'actor_name', width: 100 },
    { title: '舊狀態', dataIndex: 'old_status', key: 'old_status', width: 70 },
    { title: '新狀態', dataIndex: 'new_status', key: 'new_status', width: 70 },
    { title: '備註', dataIndex: 'comment', key: 'comment' },
    { title: '時間', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-TW') : '' },
  ];

  return (
    <Modal
      title={`審核歷史 - ${requestNo}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={720}
    >
      <Table columns={columns} dataSource={data} rowKey="id" size="small" loading={loading} pagination={false} />
    </Modal>
  );
};

const J215Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const permissions = useModulePermission(MODULE_CODE);
  const {
    mode, isEditing, loading, records, current, currentIdx,
    orderNo, processDate, setProcessDate,
    companyCode, setCompanyCode, subcontractorCode, setSubcontractorCode,
    deliveryDate, setDeliveryDate, packingCode, setPackingCode,
    unit, setUnit, pricingMode, setPricingMode,
    currency, setCurrency, processingType, setProcessingType,
    weavingSubtype, setWeavingSubtype,
    remarkCode, setRemarkCode, remarkContent, setRemarkContent,
    memo, setMemo, totalQty, totalAmt, totalItems, status,
    detailSpec, setDetailSpec,
    details, addDetailRow, removeDetailRow, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch, loadRecords,
  } = useRdSubcontractForm();

  const [searchText, setSearchText] = useState('');
  const [historyVisible, setHistoryVisible] = useState(false);

  // Load review record if ?review= is present
  React.useEffect(() => {
    if (reviewId) {
      quickSearch(reviewId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);

  const handleDelete = useCallback(() => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除', okText: '刪除', okType: 'danger', cancelText: '取消',
      content: `確定要刪除加工單號「${current.fj610_01}」？`,
      onOk: doDelete,
    });
  }, [current, doDelete]);

  // Approval handlers
  const handleSubmit = useCallback(() => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將加工單 ${current.fj610_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await rdSubcontractApi.submitForApproval(current.fj610_01);
          message.success(resp.message || '送審成功');
          await loadRecords();
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗');
          message.error(msg);
        }
      },
    });
  }, [current, isEditing, loadRecords]);

  const handleApprove = useCallback(() => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准加工單 ${current.fj610_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await rdSubcontractApi.approve(current.fj610_01);
          message.success(resp.message || '核准成功');
          await loadRecords();
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
          message.error(msg);
        }
      },
    });
  }, [current, isEditing, loadRecords]);

  const handleReject = useCallback(() => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回加工單 ${current.fj610_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await rdSubcontractApi.reject(current.fj610_01);
          message.success(resp.message || '退回成功');
          await loadRecords();
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗');
          message.error(msg);
        }
      },
    });
  }, [current, isEditing, loadRecords]);

  const handleWriteoff = useCallback(() => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${current.fj610_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await rdSubcontractApi.writeoff(current.fj610_01);
          message.success(resp.message || '沖銷成功');
          await loadRecords();
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  }, [current, isEditing, loadRecords]);

  const handleViewHistory = useCallback(() => {
    if (!current) return;
    setHistoryVisible(true);
  }, [current]);

  // SharedActionBar handlers
  const handleAddAction = useCallback(() => {
    if (isEditing && mode === 'add') {
      save();
    } else {
      startAdd();
    }
  }, [isEditing, mode, save, startAdd]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      cancel();
    } else {
      startEdit();
    }
  }, [isEditing, cancel, startEdit]);

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  // Detail table columns
  const detailColumns = [
    { title: <span data-contract-field="fj611_02">項次</span>, dataIndex: 'fj611_02', width: 60, render: (_: any, __: any, i: number) => i + 1 },
    {
      title: <span data-contract-field="fj611_11">產品主碼</span>, dataIndex: 'fj611_11', width: 100,
      render: (v: string, _: any, i: number) => isEditing
        ? <Input size="small" value={v} maxLength={12} onChange={e => updateDetail(i, 'fj611_11', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fj611_111">產品副碼</span>, dataIndex: 'fj611_111', width: 100,
      render: (v: string, _: any, i: number) => isEditing
        ? <Input size="small" value={v} maxLength={12} onChange={e => updateDetail(i, 'fj611_111', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fj611_12">色號</span>, dataIndex: 'fj611_12', width: 80,
      render: (v: string, _: any, i: number) => isEditing
        ? <Input size="small" value={v} maxLength={12} onChange={e => updateDetail(i, 'fj611_12', e.target.value)} />
        : v,
    },
    { title: <span data-contract-field="fj611_121">色名</span>, dataIndex: 'fj611_121', width: 100 },
    {
      title: <span data-contract-field="fj611_14">成品量</span>, dataIndex: 'fj611_14', width: 100,
      render: (v: number, _: any, i: number) => isEditing
        ? <InputNumber size="small" value={v} min={0} style={{ width: '100%' }}
            onChange={val => updateDetail(i, 'fj611_14', val || 0)} />
        : v,
    },
    {
      title: <span data-contract-field="fj611_15">未稅單價</span>, dataIndex: 'fj611_15', width: 100,
      render: (v: number, _: any, i: number) => {
        const disabled = !isEditing || pricingMode === 2;
        return <InputNumber size="small" value={v} min={0} precision={4} style={{ width: '100%' }}
          disabled={disabled} onChange={val => updateDetail(i, 'fj611_15', val || 0)} />;
      },
    },
    {
      title: <span data-contract-field="fj611_16">未稅金額</span>, dataIndex: 'fj611_16', width: 100,
      render: (v: number, _: any, i: number) => {
        const disabled = !isEditing || pricingMode === 1;
        return <InputNumber size="small" value={v} min={0} precision={3} style={{ width: '100%' }}
          disabled={disabled} onChange={val => updateDetail(i, 'fj611_16', val || 0)} />;
      },
    },
    {
      title: <span data-contract-field="fj611_151">含稅單價</span>, dataIndex: 'fj611_151', width: 100,
      render: (v: number) => <span>{v?.toFixed(4) ?? ''}</span>,
    },
    {
      title: <span data-contract-field="fj611_161">含稅金額</span>, dataIndex: 'fj611_161', width: 100,
      render: (v: number) => <span>{v?.toFixed(3) ?? ''}</span>,
    },
    {
      title: <span data-contract-field="fj611_12a">CBNO.</span>, dataIndex: 'fj611_12a', width: 80,
      render: (v: string, _: any, i: number) => isEditing
        ? <Input size="small" value={v} maxLength={12} onChange={e => updateDetail(i, 'fj611_12a', e.target.value)} />
        : v,
    },
    ...(isEditing ? [{
      title: '', width: 40,
      render: (_: any, __: any, i: number) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />}
          onClick={() => removeDetailRow(i)} />
      ),
    }] : []),
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-6xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          研發委外託工單 (J215)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
            {status && status !== 'N' && (
              <span className="ml-2 text-orange-500">[{statusLabels[status] || status}]</span>
            )}
          </span>
        </h2>

        {/* Quick Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="輸入單號或公司代號查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && quickSearch(searchText.trim())}
            disabled={isEditing} style={{ width: 300 }} maxLength={20} />
          <Button onClick={() => searchText.trim() && quickSearch(searchText.trim())} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation is now handled by SharedActionBar */}

        {/* Master Fields */}
        <div className="space-y-3 mb-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_01">加工單號</label>
              <Input value={orderNo} disabled style={{ width: '100%' }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_11">加工日期 <span className="text-red-500">*</span></label>
              <Input value={processDate} onChange={e => setProcessDate(e.target.value)}
                disabled={!isEditing} placeholder="YYYY-MM-DD" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_12">公司代號 <span className="text-red-500">*</span></label>
              <LookupField lookupType="customer" value={companyCode}
                onChange={v => setCompanyCode(v)} onCodeConfirm={v => setCompanyCode(v)}
                maxLength={12} disabled={!isEditing} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_14">產品規格 <span className="text-red-500">*</span></label>
              <LookupField lookupType="contractorCode" value={subcontractorCode}
                onChange={v => setSubcontractorCode(v)} onCodeConfirm={v => setSubcontractorCode(v)}
                maxLength={6} disabled={!isEditing} />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_15">預計交貨日 <span className="text-red-500">*</span></label>
              <Input value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)}
                disabled={!isEditing} placeholder="YYYY-MM-DD" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_16">包裝代號 <span className="text-red-500">*</span></label>
              <LookupField lookupType="packing" value={packingCode}
                onChange={v => setPackingCode(v)} onCodeConfirm={v => setPackingCode(v)}
                maxLength={2} disabled={!isEditing} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_21">單位 <span className="text-red-500">*</span></label>
              <Input value={unit} onChange={e => setUnit(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_30">幣別</label>
              <LookupField lookupType="currency" value={currency}
                onChange={v => setCurrency(v)} onCodeConfirm={v => setCurrency(v)}
                maxLength={3} disabled={!isEditing} style={{ width: 120 }} />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_24">計價方式 <span className="text-red-500">*</span></label>
              <Select value={pricingMode || undefined} onChange={v => setPricingMode(v)}
                disabled={!isEditing} style={{ width: 150 }} placeholder="請選擇">
                <Select.Option value={1}>計量</Select.Option>
                <Select.Option value={2}>計缸</Select.Option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_48">加工類別</label>
              <Radio.Group value={processingType} onChange={e => setProcessingType(e.target.value)} disabled={!isEditing}>
                <Radio value={1}>織造</Radio>
                <Radio value={2}>染整</Radio>
                <Radio value={3}>防水透濕</Radio>
              </Radio.Group>
            </div>
            {processingType === 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_50">織造子類型</label>
                <Radio.Group value={weavingSubtype} onChange={e => setWeavingSubtype(e.target.value)} disabled={!isEditing}>
                  <Radio value={1}>針織</Radio>
                  <Radio value={2}>平織</Radio>
                </Radio.Group>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_17">總託工數量</label>
              <InputNumber value={totalQty} disabled style={{ width: 150 }} precision={3} />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_22">金額合計</label>
              <InputNumber value={totalAmt} disabled style={{ width: 150 }} precision={3} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_18">備註代號</label>
              <LookupField lookupType="remarkTemplate" value={remarkCode}
                onChange={v => setRemarkCode(v)} onCodeConfirm={v => setRemarkCode(v)}
                maxLength={2} disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_19">備註內容</label>
              <TextArea value={remarkContent} onChange={e => setRemarkContent(e.target.value)}
                disabled={!isEditing} rows={2} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_20">送貨地點</label>
            <TextArea value={memo} onChange={e => setMemo(e.target.value)}
              disabled={!isEditing} rows={2} />
          </div>
        </div>

        {/* Detail Section Header */}
        <div className="grid grid-cols-4 gap-4 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj610_23">總項數</label>
            <InputNumber value={totalItems} disabled style={{ width: 80 }} />
          </div>
        </div>

        {/* Detail Table */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold">託工明細</h3>
            {isEditing && (
              <Button size="small" icon={<PlusOutlined />} onClick={addDetailRow}>新增明細</Button>
            )}
          </div>
          <Table dataSource={details as any[]} columns={detailColumns} size="small"
            pagination={false} rowKey={(_, i) => String(i)} scroll={{ x: 1100 }} />
        </div>

        {/* Detail Product Spec */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-contract-field="fj611_17">產品規格</label>
          <TextArea value={detailSpec} onChange={e => setDetailSpec(e.target.value)}
            disabled={!isEditing} rows={3} />
        </div>

        {/* Audit Fields */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4">
            入單人員: {current.fj610_91} | 修改日期: {current.fj610_92 || '-'} | 修改人員: {current.fj610_93}
          </div>
        )}

        {/* SharedActionBar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!current}
          currentIdx={currentIdx}
          recordCount={records.length}
          onNavFirst={navFirst}
          onNavPrev={navPrev}
          onNavNext={navNext}
          onNavLast={navLast}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onSave={save}
          onCancel={cancel}
          onDelete={handleDelete}
          approval={{
            statusCode: status || '',
            isApproved: status === 'P',
            isPending: ['D', 'S'].includes(status || ''), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: ['P', 'D', 'S'].includes(status || ''),
          }}
          onSubmit={handleSubmit}
          onApprove={handleApprove}
          onReject={handleReject}
          onWriteoff={handleWriteoff}
          onHistory={handleViewHistory}
          onExit={() => navigate('/erp')}
          canCreate={permissions.canCreate}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
          canApprove={permissions.canApprove}
          acceptanceActionIds={{
            create: 'PWJ215-ACT-CREATE',
            update: 'PWJ215-ACT-UPDATE',
            delete: 'PWJ215-ACT-DELETE',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwj215f1AcceptanceManifest}
          records={records}
          selectedKeys={current?.fj610_01 ? [current.fj610_01] : []}
          keyOf={(item) => item.fj610_01}
          queryExact={(key) => rdSubcontractApi.get(key)}
          lastApiResponse={current}
          loading={loading}
          onAutoFill={navFirst}
          autoFillDisabled={records.length === 0}
          autoFillReason="載入正式 API 已存在的第一筆研發託工單。"
          screenDraft={{
            master: {
              orderNo, processDate, companyCode, subcontractorCode, deliveryDate,
              packingCode, unit, pricingMode, currency, processingType,
              weavingSubtype, remarkCode, remarkContent, memo, totalQty,
              totalAmt, totalItems, status, detailSpec,
            },
            details,
          }}
        />
      </div>

      <ApprovalHistoryModal
        visible={historyVisible}
        requestNo={current?.fj610_01 || ''}
        onClose={() => setHistoryVisible(false)}
      />
    </Spin>
  );
};

export default J215Form;
